import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";

const displayStock = (v) => (typeof v === "number" ? (v < 0 ? "Non géré" : v) : "—");

const fmtDate = (ts) => {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleString();
};

// // ✅ variantKey = size (40,41,42…)
// const variantKeyFromVariant = (v) => String(v?.size || "default");

export default function ProductStockSheet() {
  const { productId } = useParams();

  const [product, setProduct] = useState(undefined); // undefined=loading, null=not found
  const [movements, setMovements] = useState([]);
  const [variantFilter, setVariantFilter] = useState("all");
  const [loadingMovs, setLoadingMovs] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const pSnap = await getDoc(doc(db, "products", productId));
        setProduct(pSnap.exists() ? { id: pSnap.id, ...pSnap.data() } : null);
      } catch (e) {
        console.error(e);
        setProduct(null);
      }
    })();
  }, [productId]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingMovs(true);
        const qMov = query(
          collection(db, "stock_movements"),
          where("productId", "==", productId),
          orderBy("createdAt", "desc")
        );
        const mSnap = await getDocs(qMov);
        setMovements(mSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        setMovements([]);
      } finally {
        setLoadingMovs(false);
      }
    })();
  }, [productId]);

  // ✅ Variantes: size/sku/stock
  const variants = useMemo(() => {
    const vars = Array.isArray(product?.variants) ? product.variants : [];
    // taille obligatoire, on dédoublonne
    const bySize = new Map();
    for (const v of vars) {
      if (!v?.size) continue;
      const key = String(v.size);
      if (!bySize.has(key)) {
        bySize.set(key, {
          key, // => size string
          size: String(v.size),
          sku: v.sku || null,
          stock: typeof v.stock === "number" ? v.stock : null,
        });
      }
    }
    return Array.from(bySize.values()).sort((a, b) => Number(a.size) - Number(b.size));
  }, [product]);

  // filtre mouvements selon variantKey (size)
  const filteredMovs = useMemo(() => {
    if (variantFilter === "all") return movements;
    return movements.filter((m) => String(m.variantKey || "default") === String(variantFilter));
  }, [movements, variantFilter]);

  // Solde calculé depuis mouvements filtrés (audit)
  const balanceFromMovements = useMemo(() => {
    const list = [...filteredMovs].reverse(); // ancien -> récent
    let bal = 0;
    for (const m of list) {
      const qty = Number(m.qty || 0);
      if (m.type === "IN") bal += qty;
      if (m.type === "OUT") bal -= qty;
      if (m.type === "ADJUST") bal += qty; // qty peut être + ou -
    }
    return bal;
  }, [filteredMovs]);

  // KPIs variantes
  const variantsKpis = useMemo(() => {
    let totalStock = 0;
    let lowCount = 0;

    for (const v of variants) {
      if (typeof v.stock === "number" && v.stock >= 0) totalStock += v.stock;
      if (typeof v.stock === "number" && v.stock >= 0 && v.stock <= 3) lowCount++;
    }
    return { totalStock, lowCount, count: variants.length };
  }, [variants]);

  // Util: label filtre variantes
  const variantLabel = (v) => {
    const parts = [];
    parts.push(`Taille ${v.size}`);
    if (v.sku) parts.push(`SKU ${v.sku}`);
    if (typeof v.stock === "number") parts.push(`stock ${displayStock(v.stock)}`);
    return parts.join(" • ");
  };

  if (product === undefined) {
    return <div className="container py-4">Chargement…</div>;
  }
  if (product === null) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Produit introuvable.</div>
      </div>
    );
  }

  const selectedVariant =
    variantFilter !== "all" && variantFilter !== "default"
      ? variants.find((v) => String(v.key) === String(variantFilter))
      : null;

  return (
    <div className="container py-4">
      <style>{`
        .soft-card { border:0; box-shadow: 0 10px 22px rgba(0,0,0,.06); border-radius: 14px; }
      `}</style>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h3 className="m-0">Fiche de stock</h3>
          <div className="text-muted">
            {product.title} — {product.reference || "—"} {product.code ? `• Code ${product.code}` : ""}
          </div>
          {selectedVariant ? (
            <div className="text-muted small mt-1">
              Filtre: <b>Taille {selectedVariant.size}</b> {selectedVariant.sku ? `• SKU ${selectedVariant.sku}` : ""}
            </div>
          ) : null}
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="text-muted">Variante</label>
          <select
            className="form-select"
            style={{ width: 320 }}
            value={variantFilter}
            onChange={(e) => setVariantFilter(e.target.value)}
          >
            <option value="all">Toutes (mouvements)</option>
            <option value="default">default (sans taille)</option>
            {variants.map((v) => (
              <option key={v.key} value={v.key}>
                {variantLabel(v)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="card soft-card p-3">
            <div className="text-muted">Stock (champ produit)</div>
            <div className="fs-4 fw-bold">{displayStock(product.stock)}</div>
            <small className="text-muted">(-1 = non géré)</small>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card soft-card p-3">
            <div className="text-muted">Stock total (tailles)</div>
            <div className="fs-4 fw-bold">{variantsKpis.totalStock}</div>
            <small className="text-muted">
              {variantsKpis.count} taille(s) • {variantsKpis.lowCount} en stock bas (≤3)
            </small>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card soft-card p-3">
            <div className="text-muted">Solde calculé (mouvements filtrés)</div>
            <div className="fs-4 fw-bold">{balanceFromMovements}</div>
            <small className="text-muted">
              Audit à partir de stock_movements {variantFilter === "all" ? "" : "(filtré)"}
            </small>
          </div>
        </div>
      </div>

      <div className="card soft-card">
        <div className="card-header d-flex justify-content-between align-items-center bg-transparent">
          <b>Variantes (tailles)</b>
          <span className="text-muted">{variants.length} taille(s)</span>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle m-0">
            <thead className="table-light">
              <tr>
                <th>Taille</th>
                <th>SKU</th>
                <th>Stock</th>
              </tr>
            </thead>
            <tbody>
              {variants.length ? (
                variants.map((v) => {
                  const low = typeof v.stock === "number" && v.stock >= 0 && v.stock <= 3;
                  return (
                    <tr key={v.key}>
                      <td className="fw-semibold">{v.size}</td>
                      <td className="text-muted">{v.sku || "—"}</td>
                      <td>
                        <span className={`badge ${low ? "text-bg-warning" : "text-bg-secondary"}`}>
                          {displayStock(v.stock)}
                        </span>
                        {low ? <span className="text-muted small ms-2">stock bas</span> : null}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-4">
                    Aucune variante.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card soft-card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center bg-transparent">
          <b>Historique des mouvements</b>
          <span className="text-muted">{loadingMovs ? "..." : `${filteredMovs.length} ligne(s)`}</span>
        </div>

        <div className="table-responsive">
          <table className="table table-striped align-middle m-0">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Qté</th>
                <th>Variante</th>
                <th>Raison</th>
                <th>Référence</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {loadingMovs ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    Chargement des mouvements…
                  </td>
                </tr>
              ) : (
                <>
                  {filteredMovs.map((m) => (
                    <tr key={m.id}>
                      <td>{fmtDate(m.createdAt)}</td>
                      <td>
                        <span
                          className={`badge ${
                            m.type === "IN" ? "text-bg-success" : m.type === "OUT" ? "text-bg-danger" : "text-bg-warning"
                          }`}
                        >
                          {m.type}
                        </span>
                      </td>
                      <td className="fw-bold">{m.qty}</td>
                      <td>{m.variantKey || "default"}</td>
                      <td>{m.reason || "—"}</td>
                      <td>{m.orderId || m.receptionId || "—"}</td>
                      <td className="text-muted">{m.note || ""}</td>
                    </tr>
                  ))}

                  {!filteredMovs.length && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Aucun mouvement.
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}